import React, { useState, useEffect } from 'react';
import { Leaf, Wheat, MapPin, Award, Plus, Check, Star } from 'lucide-react';

const BADGE_DISPLAY = [
  { key: 'badge_artesano', icon: '🔨', label: { es: 'Artesano', en: 'Artisan', cat: 'Artesà', fr: 'Artisanal' } },
  { key: 'badge_local', icon: '📍', label: { es: 'Local', en: 'Local', cat: 'Local', fr: 'Local' } },
  { key: 'badge_ecologico', icon: '🌿', label: { es: 'Eco', en: 'Organic', cat: 'Eco', fr: 'Bio' } },
  { key: 'badge_edicion_limitada', icon: '⏳', label: { es: 'Limitada', en: 'Limited', cat: 'Limitada', fr: 'Limitée' } },
  { key: 'badge_premium', icon: '💎', label: { es: 'Premium', en: 'Premium', cat: 'Premium', fr: 'Premium' } },
  { key: 'badge_recomendado', icon: '👍', label: { es: 'Recomendado', en: 'Recommended', cat: 'Recomanat', fr: 'Recommandé' } },
  { key: 'badge_natural', icon: '🍃', label: { es: 'Natural', en: 'Natural', cat: 'Natural', fr: 'Naturel' } },
  { key: 'badge_sin_alcohol', icon: '🚫', label: { es: 'Sin Alcohol', en: 'Non-Alcoholic', cat: 'Sense Alcohol', fr: 'Sans Alcool' } },
  { key: 'badge_vegano', icon: '🌱', label: { es: 'Vegano', en: 'Vegan', cat: 'Vegà', fr: 'Végan' } },
  { key: 'badge_sin_gluten', icon: '🌾', label: { es: 'Sin Gluten', en: 'Gluten-Free', cat: 'Sense Gluten', fr: 'Sans Gluten' } },
  { key: 'badge_bestseller', icon: '⭐', label: { es: 'Bestseller', en: 'Bestseller', cat: 'Més Venut', fr: 'Best-seller' } },
  { key: 'badge_nuevo', icon: '✨', label: { es: 'Nuevo', en: 'New', cat: 'Nou', fr: 'Nouveau' } },
];

const ProductCard = ({ product, index, onClick, onQuickAdd, isJustAdded, getImage, localize, language, t, isDark }) => {
  const [showAdded, setShowAdded] = useState(false);

  useEffect(() => {
    if (isJustAdded) {
      setShowAdded(true);
      const timer = setTimeout(() => setShowAdded(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isJustAdded]);

  if (!product) return null;

  const activeBadges = BADGE_DISPLAY.filter(b => product[b.key]);
  
  const getOriginSealText = () => {
    if (!product.origin_seal) return null;
    if (typeof product.origin_seal === 'string') return product.origin_seal;
    return product.origin_seal[language] || product.origin_seal.es || product.origin_seal.cat || '';
  };
  
  const originSealText = getOriginSealText();
  const hasLegacyVegan = product.is_vegan || product.badge_vegano;
  const hasLegacyGlutenFree = product.is_gluten_free || product.badge_sin_gluten;
  const hasLegacyLocal = product.is_local || product.badge_local;
  const hasLegacyPremium = product.is_premium || product.badge_premium;
  const hasBestseller = product.is_featured || product.badge_bestseller;

  return (
    <div
      className={`product-card text-left animate-fade-up cursor-pointer transition-colors duration-300 ${isDark ? 'dark-card' : ''}`}
      style={{ 
        animationDelay: `${Math.min((index + 1) * 50, 500)}ms`,
        backgroundColor: isDark ? '#2a2a2a' : undefined,
        borderColor: isDark ? '#404040' : undefined
      }}
      data-testid={`product-card-${product.id}`}
    >
      <div onClick={onClick}>
        <div className="product-card-image relative">
          <img 
            src={getImage(product, index)}
            alt={localize(product.name)}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          
          {originSealText && (
            <div 
              className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
              style={{ 
                background: 'linear-gradient(135deg, #D4AF37 0%, #F5D061 50%, #D4AF37 100%)',
                color: '#1a1a1a',
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                textShadow: '0 1px 1px rgba(255,255,255,0.3)'
              }}
            >
              {originSealText}
            </div>
          )}
          
          {!originSealText && hasLegacyPremium && (
            <div 
              className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
              style={{ 
                background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #B8860B 100%)',
                color: '#FFF',
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
              }}
            >
              <Star size={10} fill="currentColor" />
            </div>
          )}
          
          {hasBestseller && (
            <div 
              className="absolute top-2 right-2 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
              style={{ 
                background: 'linear-gradient(135deg, #B78C3C 0%, #D4AF37 100%)',
                color: '#FFF',
                boxShadow: '0 2px 8px rgba(183, 140, 60, 0.4)',
                animation: 'pulse 2s infinite'
              }}
            >
              Top Ventas
            </div>
          )}
          
          {product.badge_nuevo && !hasBestseller && (
            <div 
              className="absolute top-2 right-2 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
              style={{ 
                background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                color: '#FFF',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)'
              }}
            >
              Nuevo
            </div>
          )}
        </div>

        <div className="product-card-body">
          {(hasLegacyVegan || hasLegacyGlutenFree || hasLegacyLocal || product.is_do_emporda || activeBadges.length > 0) && (
            <div className="dietary-badges flex flex-wrap gap-1 mb-1">
              {hasLegacyVegan && (
                <span className="dietary-badge badge-vegan" title={t('vegan')}>
                  <Leaf size={10} />
                </span>
              )}
              {hasLegacyGlutenFree && (
                <span className="dietary-badge badge-gluten-free" title={t('glutenFree')}>
                  <Wheat size={10} />
                </span>
              )}
              {hasLegacyLocal && (
                <span className="dietary-badge badge-local" title={t('local')}>
                  <MapPin size={10} />
                </span>
              )}
              {product.is_do_emporda && (
                <span className="dietary-badge badge-do" title={t('doEmporda')}>
                  <Award size={10} />
                </span>
              )}
              {activeBadges
                .filter(b => !['badge_vegano', 'badge_sin_gluten', 'badge_local', 'badge_premium', 'badge_bestseller', 'badge_nuevo'].includes(b.key))
                .slice(0, 3)
                .map(badge => (
                  <span 
                    key={badge.key}
                    className="text-xs px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-700"
                    title={badge.label[language] || badge.label.es}
                  >
                    {badge.icon}
                  </span>
                ))
              }
            </div>
          )}

          <h3 
            className="product-card-name line-clamp-2 transition-colors duration-300"
            style={{ color: isDark ? '#f5f5f5' : undefined }}
          >
            {localize(product.name)}
          </h3>
        </div>
      </div>
      
      <div className="px-4 pb-5 flex items-center" style={{ borderTop: '1px solid rgba(183, 140, 60, 0.12)', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
        <button
          onClick={onQuickAdd}
          className={`quick-add-btn-pill ${showAdded ? 'added' : ''}`}
          data-testid={`quick-add-${product.id}`}
          aria-label={t('add')}
        >
          <span className="qa-price">{product.price.toFixed(2)} €</span>
          <span className="qa-icon">{showAdded ? <Check size={16} /> : <Plus size={16} />}</span>
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
